import { KeyRound } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { api, ApiError } from '../lib/api';

const copy = {
  title: { en: 'Change password', es: 'Cambiar contraseña' },
  current: { en: 'Current password', es: 'Contraseña actual' },
  next: { en: 'New password', es: 'Nueva contraseña' },
  confirm: { en: 'Confirm new password', es: 'Confirmar nueva contraseña' },
  hint: {
    en: 'Use 6–128 characters.',
    es: 'Use entre 6 y 128 caracteres.',
  },
  mismatch: {
    en: 'New passwords do not match.',
    es: 'Las nuevas contraseñas no coinciden.',
  },
  save: { en: 'Update password', es: 'Actualizar contraseña' },
  saving: { en: 'Updating…', es: 'Actualizando…' },
  cancel: { en: 'Cancel', es: 'Cancelar' },
  success: {
    en: 'Password updated. You remain signed in on this device.',
    es: 'Contraseña actualizada. Sigue conectado en este dispositivo.',
  },
  networkError: {
    en: "We couldn't connect to the server. Check your connection and try again.",
    es: 'No pudimos conectar con el servidor. Verifique su conexión e inténtelo de nuevo.',
  },
  wrongPassword: {
    en: 'Your current password is incorrect. Please try again.',
    es: 'Su contraseña actual es incorrecta. Inténtelo de nuevo.',
  },
  genericError: {
    en: "We couldn't update your password. Please try again.",
    es: 'No pudimos actualizar su contraseña. Inténtelo de nuevo.',
  },
} as const;

export function ChangePasswordForm({
  language = 'en',
  onSuccess,
  onCancel,
}: {
  language?: 'en' | 'es';
  onSuccess?: () => void;
  onCancel?: () => void;
}) {
  const t = (key: keyof typeof copy) => copy[key][language];
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const currentPassword = String(form.get('currentPassword') ?? '');
    const newPassword = String(form.get('newPassword') ?? '');
    const confirmPassword = String(form.get('confirmPassword') ?? '');
    if (newPassword !== confirmPassword) {
      setError(t('mismatch'));
      return;
    }
    setError('');
    setSaving(true);
    try {
      await api('/auth/password', {
        method: 'PUT',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      toast.success(t('success'));
      event.currentTarget.reset();
      onSuccess?.();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'NETWORK_ERROR') setError(t('networkError'));
        else if (err.code === 'UNAUTHORIZED') setError(t('wrongPassword'));
        else setError(language === 'es' ? t('genericError') : err.message);
      } else {
        setError(t('genericError'));
      }
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="max-w-sm">
      {error && (
        <div className="schd-error mb-4" role="alert">
          {error}
        </div>
      )}
      <label className="block text-sm font-semibold" htmlFor="currentPassword">
        {t('current')}
      </label>
      <input
        id="currentPassword"
        name="currentPassword"
        type="password"
        autoComplete="current-password"
        required
        maxLength={128}
        disabled={saving}
        className="mt-1 min-h-11 w-full rounded-control border px-3"
      />
      <label className="mt-4 block text-sm font-semibold" htmlFor="newPassword">
        {t('next')}
      </label>
      <input
        id="newPassword"
        name="newPassword"
        type="password"
        autoComplete="new-password"
        required
        minLength={6}
        maxLength={128}
        disabled={saving}
        className="mt-1 min-h-11 w-full rounded-control border px-3"
      />
      <p className="mt-1 text-xs text-ink/60">{t('hint')}</p>
      <label className="mt-4 block text-sm font-semibold" htmlFor="confirmPassword">
        {t('confirm')}
      </label>
      <input
        id="confirmPassword"
        name="confirmPassword"
        type="password"
        autoComplete="new-password"
        required
        minLength={6}
        maxLength={128}
        disabled={saving}
        className="mt-1 min-h-11 w-full rounded-control border px-3"
      />
      <div className="mt-6 flex gap-2">
        <button disabled={saving} className="schd-button-primary">
          {saving ? t('saving') : t('save')}
        </button>
        {onCancel && (
          <button type="button" className="schd-button-secondary" onClick={onCancel} disabled={saving}>
            {t('cancel')}
          </button>
        )}
      </div>
    </form>
  );
}

export function ChangePasswordModal({
  onClose,
  language = 'en',
}: {
  onClose: () => void;
  language?: 'en' | 'es';
}) {
  const t = (key: keyof typeof copy) => copy[key][language];
  const dialogRef = useRef<HTMLDivElement>(null);
  const [closing, setClosing] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(closeTimeoutRef.current), []);
  const requestClose = useCallback(() => {
    if (closeTimeoutRef.current) return;
    setClosing(true);
    closeTimeoutRef.current = setTimeout(onClose, 120);
  }, [onClose]);
  useEffect(() => {
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [requestClose]);
  return (
    <div
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-[70] flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
        className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} schd-card w-full max-w-sm`}
      >
        <div className="flex items-center gap-2">
          <KeyRound className="size-5 text-burgundy" aria-hidden="true" />
          <h2 id="change-password-title" className="font-display text-xl">
            {t('title')}
          </h2>
        </div>
        <div className="mt-4">
          <ChangePasswordForm language={language} onSuccess={requestClose} onCancel={requestClose} />
        </div>
      </div>
    </div>
  );
}
